import { useState } from 'react';
import { Button } from '../ui/button';
import { Label } from '../ui/label';
import TimeSelectorRow from './TimeSelectorRow';
import ToggleSettingRow from '../ToggleSettingRow';
import type { NotificationSetting } from "../../types/notification-settings";

const defaultSettings: NotificationSetting = {
  notifyNewMessages: false,
  notifyCalls: false,
  notifyNewStories: false,
  mute: {
    monday: { enable: false, start: "00:00", end: "24:00" },
    tuesday: { enable: false, start: "00:00", end: "24:00" },
    wednesday: { enable: false, start: "00:00", end: "24:00" },
    thursday: { enable: false, start: "00:00", end: "24:00" },
    friday: { enable: false, start: "00:00", end: "24:00" },
    saturday: { enable: false, start: "00:00", end: "24:00" },
    sunday: { enable: false, start: "00:00", end: "24:00" },
  },
};

interface NotificationSettingsProps {
  initialSettings: NotificationSetting | null;
  onSave?: (settings: Partial<NotificationSetting>) => void;
}

export default function NotificationSettings({ initialSettings, onSave }: NotificationSettingsProps) {
  const [settings, setSettings] = useState<NotificationSetting>(initialSettings || defaultSettings);

  const handleToggleChange = (id: 'notifyNewMessages' | 'notifyCalls' | 'notifyNewStories', checked: boolean) => {
    setSettings(prev => ({ ...prev, [id]: checked }));
  };

  const handleTimeChange = (day: keyof NotificationSetting['mute'], timeType: 'start' | 'end', time: string, checked: boolean) => {
    setSettings(prev => ({
      ...prev,
      mute: {
        ...prev.mute,
        [day]: { ...prev.mute[day], enable: checked, [timeType]: time }
      }
    }));
  };

  const handleSave = async () => {
    console.log("Saving notification settings:", settings);
    onSave?.(settings);
  };

  const handleCancel = () => {
    setSettings(initialSettings || defaultSettings);
  };

  return (
    <div className="jr-card jr-screen-pad mt-4 flex flex-col overflow-hidden px-0">
      <ToggleSettingRow
        id="notifyNewMessages"
        label="Notify for New Messages"
        checked={settings.notifyNewMessages}
        onCheckedChange={(checked) => handleToggleChange('notifyNewMessages', checked)}
      />
      <ToggleSettingRow
        id="notifyCalls"
        label="Notify for Calls"
        checked={settings.notifyCalls}
        onCheckedChange={(checked) => handleToggleChange('notifyCalls', checked)}
      />
      <ToggleSettingRow
        id="notifyNewStories"
        label="Notify for New Stories"
        checked={settings.notifyNewStories}
        onCheckedChange={(checked) => handleToggleChange('notifyNewStories', checked)}
      />

      <div className="px-4 py-4">
        <Label className="text-base font-semibold text-[var(--jr-text)]">Mute Notifications by Day or Time:</Label>
        <div className="mt-2 flex flex-col">
          {Object.entries(settings.mute).map(([day, config]) => (
            <TimeSelectorRow
              key={day}
              label={day.charAt(0).toUpperCase() + day.slice(1)}
              enable={config.enable}
              start={config.start}
              end={config.end}
              onEnableChange={(checked) => handleTimeChange(day as keyof NotificationSetting['mute'], 'start', '', checked)}
              onStartChange={(time) => handleTimeChange(day as keyof NotificationSetting['mute'], 'start', time, config.enable)}
              onEndChange={(time) => handleTimeChange(day as keyof NotificationSetting['mute'], 'end', time, config.enable)}
            />
          ))}
        </div>
      </div>

      <div className="flex space-x-4 p-4">
        <Button onClick={handleSave} className="jr-action-primary h-auto flex-1 min-h-11">
          Save
        </Button>
        <Button onClick={handleCancel} variant="outline" className="jr-action-secondary h-auto flex-1 min-h-11">
          Cancel
        </Button>
      </div>
    </div>
  );
}
